import React, { FC, useState } from 'react';
import styled, { css } from 'styled-components';
import { currency } from '../../utils';
import { Typography } from '../Typography';
import { Kfh_MonthlyStatisticsType } from '../../types/graphqlTypes';
import { Panel } from '../Panel';
import { DataVisualPanel } from './DataVisualPanel';
import { Divider } from '../Divider';
import { Spacer } from '../Spacer';
import { Select } from '../forms/Select';
import { Alert } from '../Alert';

interface AverageSoldPricesByPeriodProps {
  monthlyStatistics: Kfh_MonthlyStatisticsType;
  locationName: string;
}

const StyledSummaryPanels = styled.div`
  ${({ theme: { space, media } }) => css`
    display: grid;
    grid-gap: ${space.md};

    // Tablet > styles

    @media (min-width: ${media.md}) {
      grid-template-columns: 1fr 1fr;
      grid-gap: ${space.lg};

      > *:first-child {
        grid-column: 1/-1;
      }
    }
  `};
`;

const StyledSelect = styled(Select)`
  ${({ theme: { space, media } }) => css`
    @media (min-width: ${media.md}) {
      width: 176px;
    }
  `};
`;

export const AverageSoldPricesByPeriod: FC<AverageSoldPricesByPeriodProps> = ({
  monthlyStatistics,
  locationName,
  ...rest
}) => {
  const initialSelectedPeriod = 12;
  const initialActiveMonthPrices = monthlyStatistics ? monthlyStatistics[initialSelectedPeriod - 1] : undefined;
  const [activeMonthPrices, setActiveMonthPrices] = useState<Kfh_MonthlyStatisticsType | undefined>(
    initialActiveMonthPrices
  );

  const handlePeriodChange = (event) => {
    const selectedPeriod = parseInt(event.target.value, 10);
    const selectedPeriodData = monthlyStatistics?.find((stat) => stat.period === selectedPeriod);
    setActiveMonthPrices(selectedPeriodData);
  };

  return (
    <DataVisualPanel
      heading={activeMonthPrices?.summary}
      source="Land Registry"
      control={
        <div>
          <StyledSelect
            id="period"
            name="period"
            onChange={handlePeriodChange}
            label="Select Period"
            defaultValue={initialSelectedPeriod}
          >
            {monthlyStatistics?.map((stat) => (
              <option value={stat.period} key={`period-${stat.period}`}>
                {stat.period} month{stat.period !== 1 && 's'}
              </option>
            ))}
          </StyledSelect>
        </div>
      }
      {...rest}
    >
      {activeMonthPrices && (
        <StyledSummaryPanels>
          {activeMonthPrices?.average > 0 ? (
            <>
              <Panel backgroundColor="grey1" heading="All properties">
                <Spacer spacing="half">
                  <Typography component="p" variant="titleLarge">
                    {currency(activeMonthPrices?.average)}
                  </Typography>
                  <p>
                    {activeMonthPrices.houseSold > activeMonthPrices.flatSold ? 'Houses' : 'Flats'} accounted for the
                    majority of recorded sales in {locationName} during this period.
                  </p>
                </Spacer>
              </Panel>
              <Panel backgroundColor="grey1" heading="Houses">
                <Spacer spacing="half">
                  <div>
                    <Typography component="p" variant="titleLarge">
                      {currency(activeMonthPrices.houseAverage)}
                    </Typography>
                    <Typography component="p" variant="bodySmall">
                      with {activeMonthPrices.houseSold} houses sold
                    </Typography>
                  </div>
                  <Divider />
                  <div>
                    <Typography component="h4" variant="titleExtraSmall">
                      Average sold prices
                    </Typography>
                    {activeMonthPrices.semiDetachedAverage !== 0 && (
                      <Typography component="p" variant="bodySmall">
                        Terraced properties - {currency(activeMonthPrices.semiDetachedAverage)}
                      </Typography>
                    )}
                    {activeMonthPrices.terraceAverage !== 0 && (
                      <Typography component="p" variant="bodySmall">
                        Semi-detached - {currency(activeMonthPrices.terraceAverage)}
                      </Typography>
                    )}
                    {activeMonthPrices.detachedAverage !== 0 && (
                      <Typography component="p" variant="bodySmall">
                        Detached properties - {currency(activeMonthPrices.detachedAverage)}
                      </Typography>
                    )}
                  </div>
                </Spacer>
              </Panel>
              <Panel backgroundColor="grey1" heading="Flats">
                <Typography component="p" variant="titleLarge">
                  {currency(activeMonthPrices.flatAverage)}
                </Typography>
                <Typography component="p" variant="bodySmall">
                  with {activeMonthPrices.flatSold} flats sold
                </Typography>
              </Panel>
            </>
          ) : (
            <Alert
              heading="All properties"
              text="No properties were sold based on Land Registry data in this period, to see another time period use the drop down on the right."
            />
          )}
        </StyledSummaryPanels>
      )}
    </DataVisualPanel>
  );
};
